// components/Layout/Layout.jsx
import React, { useState } from "react";
import { useVideo } from "../../context/VideoContext";
import { useTheme } from "../../context/ThemeContext";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import InstallPWAButton from "../InstallPWAButton/InstallPWAButton";
import DesktopInstallBanner from "./DesktopInstallBanner";
import UserSettingsModal from "../UserSettingsModal/UserSettingsModal";
import ContactUsModal from "../ContactUsModal/ContactUsModal";
import HelpModal from "../HelpModal/HelpModal";
import Logo from "../common/Logo/Logo";
import { getNewToken } from "../../utils/handleJWT";
import "./Layout.css";
import EnamadWidget from "../common/EnamadWidget/EnamadWidget";
import IOSFormatWarning from "../VideoPlayer/IOSFormatWarning";

const Layout = () => {
  const { videoFile } = useVideo();
  const { theme, toggleTheme } = useTheme();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [loadingLeitner, setLoadingLeitner] = useState(false);
  const [showDesktopBanner, setShowDesktopBanner] = useState(true); // ⬅️ state جدید
  const [iosWarning, setIosWarning] = useState({
    isOpen: false,
    fileExtension: "",
  });

  const handleLeitnerClick = async () => {
    setLoadingLeitner(true);

    try {
      const result = await getNewToken();

      if (result.success && result.token) {
        const leitnerUrl = `https://leitnerpwa.umlang.com/tokenLogin?token=${result.token}`;
        window.open(leitnerUrl, "_blank");
      } else {
        alert(result.error || "خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.");

        if (result.shouldLogout) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      alert("خطا در باز کردن لایتنر. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoadingLeitner(false);
    }
  };

  return (
    <div className="layout">
      <header className="layout-header">
        <div className="header-left">
          <div className="logo-container">
            <Logo width={40} height={40} />
          </div>
          <h1 className="header-title">
            UMLANG
            <span className="title-full"> | پلیر پیشرفته یادگیری زبان</span>
          </h1>
          <div className="theme-toggle-wrapper">
            <label className="theme-switch">
              <input
                type="checkbox"
                checked={theme === "light"}
                onChange={toggleTheme}
                title={theme === "dark" ? "حالت روشن" : "حالت تاریک"}
              />
              <span className="theme-switch-slider">
                <span className="switch-icon switch-sun"><i className="fas fa-sun"></i></span>
                <span className="switch-icon switch-moon"><i className="fas fa-moon"></i></span>
              </span>
            </label>
          </div>
        </div>

        <div className="header-buttons">
          <button
            className="btn-header"
            onClick={handleLeitnerClick}
            title="لایتنر"
            disabled={loadingLeitner}
          >
            {loadingLeitner ? (
              <i className="fas fa-spinner fa-spin"></i>
            ) : (
              <i className="fas fa-layer-group"></i>
            )}
            <span className="btn-text">لایتنر</span>
          </button>

          <button
            className="btn-header"
            onClick={() => window.open("https://app.umlang.com", "_blank")}
            title="دانلود اپ موبایل"
          >
            <i className="fas fa-mobile-alt"></i>
            <span className="btn-text">دانلود اپ</span>
          </button>

          <button
            className="btn-header"
            onClick={() => setIsHelpModalOpen(true)}
            title="راهنما"
          >
            <i className="fas fa-question-circle"></i>
            <span className="btn-text">راهنما</span>
          </button>

          <button
            className="btn-header"
            onClick={() => setIsContactModalOpen(true)}
            title="تماس با ما"
          >
            <i className="fas fa-phone-alt"></i>
            <span className="btn-text">تماس با ما</span>
          </button>

          <button
            className="btn-header"
            onClick={() => setIsSettingsModalOpen(true)}
            title="پروفایل"
          >
            <i className="fas fa-user-circle"></i>
            <span className="btn-text">پروفایل</span>
          </button>
        </div>
      </header>

      <main className="main-content">
        <div className="player-section">
          {/* ⬅️ بنر جدید نصب برای دسکتاپ - فقط وقتی ویدیو انتخاب نشده */}
          {!videoFile && showDesktopBanner && (
            <DesktopInstallBanner onClose={() => setShowDesktopBanner(false)} />
          )}

          <VideoPlayer
            onOpenHelpModal={() => setIsHelpModalOpen(true)}
            onIOSWarning={setIosWarning}
          />

          {!videoFile && (
            <div className="instructions">
              <div className="instruction-card">
                <InstallPWAButton />
              </div>

              <div className="instruction-card shortcuts-teaser-card">
                <i className="fas fa-keyboard"></i>
                <h3>شورتکات‌های هوشمند</h3>

                <div className="shortcut-mini-list">
                  <div className="shortcut-mini-row">
                    <span>نماش/پنهان:</span>
                    <span>زیرنویس ۱</span>
                    <span className="key-badge-sm">T</span>
                    <span className="shortcut-divider">|</span>
                    <span>زیرنویس ۲</span>
                    <span className="key-badge-sm">Y</span>
                  </div>

                  <div className="shortcut-mini-row">
                    <span>زیرنویس:</span>

                    <span>بعدی</span>
                    <span className="key-badge-sm">D</span>
                    <span className="shortcut-divider">|</span>
                    <span>تکرار</span>
                    <span className="key-badge-sm">S</span>
                    <span className="shortcut-divider">|</span>
                    <span>قبلی</span>
                    <span className="key-badge-sm">A</span>
                  </div>
                </div>

                <button
                  className="shortcuts-help-link"
                  onClick={() => setIsHelpModalOpen(true)}
                >
                  <i className="fas fa-question-circle"></i>
                  <span>لیست کامل شورتکات‌ها</span>
                </button>
              </div>

              <div className="instruction-card enamad-card">
                <EnamadWidget />
                <h3>نماد اعتماد</h3>
                <p>دارای نماد اعتماد الکترونیکی</p>
              </div>
            </div>
          )}
        </div>
      </main>

      <UserSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      <ContactUsModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      <IOSFormatWarning
        isOpen={iosWarning.isOpen}
        fileExtension={iosWarning.fileExtension}
        onClose={() => setIosWarning({ isOpen: false, fileExtension: "" })}
      />
    </div>
  );
};

export default Layout;
